import {
  Create,
  SimpleForm,
  Toolbar,
  SaveButton,
  useTranslate,
  useRedirect,
  useNotify,
} from 'react-admin';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { ModelConfigForm } from './ModelConfigForm';
import { transformModelConfig } from './modelConfigFormHelpers';
import { resolveErrorCode } from './errorMapping';

const CreateToolbar = () => (
  <Toolbar sx={{ backgroundColor: '#FAFAFA', borderTop: '1px solid #E0E0E0', px: { xs: 2, sm: 3 }, py: 2 }}>
    <SaveButton variant="contained" />
  </Toolbar>
);

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      <IconButton
        onClick={() => redirect('list', 'ai-model-configs')}
        size="large"
        sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        title={translate('resources.ai-model-configs.create.backToList', { _: 'Back to model configs' })}
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
          {translate('resources.ai-model-configs.create.title', { _: 'Create Model Config' })}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
          {translate('resources.ai-model-configs.create.subtitle', {
            _: 'Add an AI model configuration',
          })}
        </Typography>
      </Box>
    </Box>
  );
};

// Create an AI model config. The transform JSON-parses the params input and strips read-only fields
// before the dataProvider POSTs. Pessimistic mode so JSON-validation + backend conflict errors
// surface before the list refreshes. onError maps the sub-plan-17 codes (REF-08 §4) to friendly
// messages and keeps the user on the form; the rare-race default conflict is the SAME 409 code.
export const ModelConfigCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  return (
    <Create
      title={false}
      component="div"
      redirect="list"
      mutationMode="pessimistic"
      transform={transformModelConfig}
      mutationOptions={{
        onSuccess: () => {
          notify('resources.ai-model-configs.notifications.created', { type: 'success' });
          redirect('list', 'ai-model-configs');
        },
        onError: (error: any) => {
          const code = resolveErrorCode(error);
          if (code === 'AI_MODEL_CONFIG_CONFLICT') {
            notify(
              translate('resources.ai-model-configs.errors.conflict', {
                _: 'A model config with this provider + model + role already exists, or a default conflict occurred. Please review and try again.',
              }),
              { type: 'error' },
            );
          } else {
            notify(error?.message || translate('resources.ai-model-configs.notifications.error.create', { _: 'Failed to create model config.' }), {
              type: 'error',
            });
          }
        },
      }}
    >
      <SimpleForm toolbar={<CreateToolbar />}>
        <Box sx={{ width: '100%' }}>
          <PageHeader />
          <ModelConfigForm />
        </Box>
      </SimpleForm>
    </Create>
  );
};

export default ModelConfigCreate;
